import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconButton } from './Button.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { cx } from './util.ts';
import './Toast.css';

export type ToastTone = 'info' | 'ok' | 'escalado' | 'error';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** ms antes de cerrarse solo; 0 = hasta que se cierre. Por defecto 6 s (los errores no se cierran solos). */
  duration?: number;
}

interface ToastEntry extends ToastInput {
  id: string;
}

export interface ToastApi {
  show: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, IconName> = {
  info: 'info',
  ok: 'estado-ok',
  escalado: 'estado-escalado',
  error: 'estado-error',
};

const DEFAULT_DURATION = 6000;

function ToastItem({ toast, onDismiss }: { toast: ToastEntry; onDismiss: (id: string) => void }) {
  const tone = toast.tone ?? 'info';
  const duration = toast.duration ?? (tone === 'error' ? 0 : DEFAULT_DURATION);

  useEffect(() => {
    if (duration <= 0) return;
    const timer = setTimeout(() => onDismiss(toast.id), duration);
    return () => clearTimeout(timer);
  }, [duration, onDismiss, toast.id]);

  return (
    <li className={cx('af-toast', `af-toast--${tone}`)}>
      <Icon name={TONE_ICON[tone]} className="af-toast__icon" />
      <div className="af-toast__text">
        <p className="af-toast__title">{toast.title}</p>
        {toast.description && <p className="af-toast__description">{toast.description}</p>}
      </div>
      <IconButton icon="cerrar" label="Cerrar aviso" onClick={() => onDismiss(toast.id)} />
    </li>
  );
}

/** Avisos breves («Corrección guardada»). La región existe siempre para que se anuncien. */
export function ToastProvider({
  children,
  label = 'Notificaciones',
}: {
  children: ReactNode;
  label?: string;
}) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const counter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((toast: ToastInput) => {
    counter.current += 1;
    const id = `toast-${counter.current}`;
    setToasts((list) => [...list, { ...toast, id }]);
    return id;
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext value={api}>
      {children}
      <section className="af-toasts" aria-label={label}>
        <ol className="af-toasts__list" aria-live="polite" aria-relevant="additions">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
          ))}
        </ol>
      </section>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast necesita un <ToastProvider> por encima');
  return api;
}
